import React, { useEffect, useState, useRef } from 'react';
import attachmentService from '../../../services/attachmentService';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_TEXT, ALLOWED_FILE_TYPES_FOR_INPUT } from './config';
import { X, Download } from 'lucide-react';
import { useNotification } from "../../../contexts/NotificationContext";
import { useLoader } from "../../../contexts/LoaderContext";

const FileUploader = ({ initialAttachments = { draftAttachmentList: [] }, onAttachmentsChange, onPendingUploadChange, disabled, taskId }) => {
  const { uploadDraftAttachments, deleteDraftAttachments, deleteAttachments, downloadAttachment } = attachmentService();
  const [files, setFiles] = useState([]);
  const [rawFiles, setRawFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const { showNotification } = useNotification();
  const { showLoader, hideLoader } = useLoader();

  useEffect(() => {
    if (initialAttachments?.draftAttachmentList?.length > 0 && files.length === 0) {
      const attachmentList = initialAttachments.draftAttachmentList;
      setFiles(attachmentList);
      setUploadedFiles({ draftAttachmentList: attachmentList });
    }
  }, [initialAttachments]);

  useEffect(() => {
    if (uploadedFiles) {
      onAttachmentsChange?.(uploadedFiles.draftAttachmentList);
    }
  }, [uploadedFiles]);

  const handleFileChange = (e) => {
    setIsFileUploaded(false);
    const selectedFiles = Array.from(e.target.files || []);
    const transformedFiles = selectedFiles.map(file => ({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      s3FilePath: ""
    }));

    const validFiles = [];
    const validRawFiles = [];
    const rejectedFiles = [];

    selectedFiles.forEach((file, idx) => {
      if (file.size <= MAX_FILE_SIZE) {
        validFiles.push(transformedFiles[idx]);
        validRawFiles.push(file);
      } else {
        rejectedFiles.push(file.name);
      }
    });

    if (rejectedFiles.length > 0) {
      alert(`The following file(s) exceed the ${MAX_FILE_SIZE_TEXT} limit and were not added:\n${rejectedFiles.join('\n')}`);
    }

    setFiles(prev => [...prev, ...validFiles]);
    setRawFiles(prev => {
      const updatedRawFiles = [...prev, ...validRawFiles];
      if (onPendingUploadChange) {
        onPendingUploadChange(updatedRawFiles.length > 0);
      }
      return updatedRawFiles;
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = async (index) => {
    const isUploadedFile = uploadedFiles?.draftAttachmentList?.[index];

    if (isUploadedFile) {
      try {
        if (isUploadedFile?.id && taskId) {
          const removePayload = {
            taskId: taskId,
            attachments: {
              [isUploadedFile.id]: isUploadedFile.s3FilePath
            }
          };
          await deleteAttachments(removePayload);
        } else {
          const removePayload = {
            draftAttachmentList: [isUploadedFile]
          };
          await deleteDraftAttachments(removePayload);
          showNotification(`Attachment(s) deleted successfully!`, "success");
        }

        const updatedUploadedFiles = [...(uploadedFiles?.draftAttachmentList || [])];
        updatedUploadedFiles.splice(index, 1);
        setUploadedFiles({ draftAttachmentList: updatedUploadedFiles });
      } catch (error) {
        showNotification(`Error in deleting attachment(s): ${error.message}`, "error");
      }
    }

    if (files.length > 0) {
      const updatedFiles = [...files];
      updatedFiles.splice(index, 1);
      setFiles(updatedFiles);
    }
    if (rawFiles.length > 0) {
      const updatedRawFiles = [...rawFiles];
      updatedRawFiles.splice(index, 1);
      setRawFiles(updatedRawFiles);
      if (onPendingUploadChange) onPendingUploadChange(updatedRawFiles.length > 0);
    }
  };

  const handleUpload = async () => {
    if (!isFileUploaded && rawFiles.length > 0) {
      try {
        showLoader();
        const formData = new FormData();
        rawFiles.forEach(file => formData.append('files', file));
        const response = await uploadDraftAttachments(formData);
        setUploadedFiles(prev => ({
          draftAttachmentList: [...(prev?.draftAttachmentList || []), ...(response.data?.draftAttachmentList || [])]
        }));
        showNotification(`Attachment(s) uploaded successfully!`, "success");
        setRawFiles([]);
        setIsFileUploaded(true);
        onPendingUploadChange && onPendingUploadChange(false);
      } catch (error) {
        showNotification(`Error in uploading attachment(s): ${error.message}`, "error");
      } finally {
        hideLoader();
      }
    }
  };

  const downloadFile = async (file) => {
    try {
      const blob = await downloadAttachment(file);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      showNotification(`Attachment downloaded successfully: ${file.fileName}`, "success");
    } catch (error) {
      showNotification(`Error in downloading attachment: ${error.message}`, "error");
    }
  }

  return (
    <div className="w-full border rounded-md p-4">
      <p className="text-xs text-gray-500 mt-1">Allowed file types: {ALLOWED_FILE_TYPES_FOR_INPUT}</p>
      <p className="text-xs text-gray-500 mt-1">Max allowed file size per file: {MAX_FILE_SIZE_TEXT}</p>

      {!disabled && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-3">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ALLOWED_FILE_TYPES_FOR_INPUT}
            onChange={handleFileChange}
            className="w-full sm:w-auto rounded-md px-2 py-2 text-sm"
          />
          <button onClick={handleUpload} className={`px-4 py-2 rounded-md text-white ${rawFiles.length ? 'bg-primary' : 'bg-gray-300 cursor-not-allowed'}`} disabled={rawFiles.length === 0}>
            Upload Files
          </button>
        </div>
      )}

      {(files.length > 0 || uploadedFiles?.draftAttachmentList?.length > 0) && (
        <ul className="mb-4 mt-4 space-y-2">
          {(files || uploadedFiles?.draftAttachmentList || []).map((file, index) => (
            <li key={index} className="flex justify-between items-center text-sm">
              <div className="truncate">{file.fileName}</div>
              <div className="flex items-center gap-2">
                {file.id && (
                  <button onClick={() => downloadFile(file)} className="text-blue-500 p-1 rounded-md bg-white">
                    <Download size={16} />
                  </button>
                )}
                {!disabled && (
                  <button onClick={() => removeFile(index)} className="text-red-500 p-1 rounded-md bg-white">
                    <X size={16} />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default FileUploader;
